import useForkRef from '@mui/utils/useForkRef';
import Button from '@mui/material/Button';
import {
  DatePicker,
  DatePickerProps,
  DatePickerFieldProps,
} from '@mui/x-date-pickers/DatePicker';
import { useValidation, validateDate } from '@mui/x-date-pickers/validation';
import {
  useSplitFieldProps,
  useParsedFormat,
  usePickerContext,
} from '@mui/x-date-pickers/hooks';
import { useLocalizationContext } from "@mui/x-date-pickers/internals";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

function ButtonDateField(props: DatePickerFieldProps) {
  const { internalProps, forwardedProps } = useSplitFieldProps(props, 'date');
  const { slotProps: _slotProps, inputRef: _inputRef, ...restForwardedProps } = forwardedProps as typeof forwardedProps & { slotProps?: unknown; inputRef?: unknown };

  const pickerContext = usePickerContext();
  const adapter = useLocalizationContext();
  const handleRef = useForkRef(pickerContext.triggerRef, pickerContext.rootRef);
  const parsedFormat = useParsedFormat();
  const { hasValidationError } = useValidation({
    validator: validateDate,
    value: pickerContext.value,
    timezone: pickerContext.timezone,
    props: internalProps,
  });

  const valueStr = pickerContext.value == null
    ? parsedFormat
    : adapter.adapter.formatByString(pickerContext.value, pickerContext.fieldFormat);

  return (
    <Button
      {...restForwardedProps}
      variant="outlined"
      color={hasValidationError ? 'error' : 'primary'}
      ref={handleRef}
      disabled={pickerContext.disabled}
      className={pickerContext.rootClassName}
      sx={{
        borderColor: 'darkgray',
        color: 'black',
        '&:hover': {
          borderColor: 'darkgray',
          backgroundColor: 'rgba(0,0,0,0.04)',
        },
        '&.Mui-disabled': {
          borderColor: 'lightgray',
          color: 'lightgray',
        }
      }}
      onClick={() => pickerContext.setOpen((prev) => !prev)}
    >
      {pickerContext.label ? `${pickerContext.label}: ${valueStr}` : valueStr}
    </Button>
  );
}

function ButtonFieldDatePicker(props: DatePickerProps) {
  return (
    <DatePicker {...props} slots={{ ...props.slots, field: ButtonDateField }} />
  );
}

export default function ButtonDatePicker(props: DatePickerProps) {
  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <ButtonFieldDatePicker {...props} />
    </LocalizationProvider>
  );
}